/**
 * AFS Advocates — Storage Helpers
 *
 * Dual-write storage: every save goes to both IndexedDB (local) and D1
 * via the Cloudflare Worker (cloud). Every load tries D1 first; if the
 * Worker is unreachable (offline / cold start), IndexedDB is used silently.
 *
 * This means:
 *  - Open on phone → cases load from D1
 *  - Open on laptop → same cases, automatically
 *  - Go offline → IndexedDB keeps everything working
 *  - Come back online → next save syncs back to D1
 */

import { db } from './db';
import type { Case, DocketEntry, Deadline, EvidenceItem, ArgumentVersion, CaseTheoryRecord, CaseTheoryHistoryEntry, CaseSummary, CloneableApplicationRecord } from '@/types';
import type { MatrimonialCaseData, MExtractionResult } from '@/matrimonial/types';
import type { BlindSpotRecord, ResearchRecord, ArgumentTemplate, DraftBufferRecord, MediaLibraryItem, RebuttalBankRecord, RebuttalBankDefeater } from './db';
import type { ClientPositionProfile, ContractClause } from '@/contracts/types';
import type { CitedCaseLibraryEntry } from '@/caseDissection/types';
import type { ArticleTopicLogEntry } from '@/articles/types';
import { AUTH_TOKEN } from '@/services/api';

// ── Config ────────────────────────────────────────────────────────────────────

const WORKER_URL   = 'https://afs-legal-rag.sobamboadeshupo.workers.dev';

function syncHeaders(): Record<string, string> {
  return {
    'Content-Type':  'application/json',
    'Authorization': `Bearer ${AUTH_TOKEN}`,
  };
}

async function syncGet(path: string): Promise<unknown | null> {
  try {
    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort(), 6000);
    try {
      const res = await fetch(`${WORKER_URL}${path}`, {
        method:  'GET',
        headers: syncHeaders(),
        signal:  controller.signal,
      });
      if (!res.ok) return null;
      return await res.json();
    } finally {
      clearTimeout(timeout);
    }
  } catch {
    return null;   // offline or cold — fall back to IndexedDB
  }
}

async function syncPut(path: string, body: unknown): Promise<void> {
  try {
    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort(), 6000);
    try {
      await fetch(`${WORKER_URL}${path}`, {
        method:  'PUT',
        headers: syncHeaders(),
        body:    JSON.stringify(body),
        signal:  controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
  } catch {
    // sync failed silently — IndexedDB already has the data
  }
}

async function syncDelete(path: string): Promise<void> {
  try {
    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort(), 6000);
    try {
      await fetch(`${WORKER_URL}${path}`, {
        method:  'DELETE',
        headers: syncHeaders(),
        signal:  controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
  } catch {
    // sync failed silently
  }
}

// ── ID generators ──────────────────────────────────────────────────────────────

export function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function cid(): string {
  return 'c_' + uid();
}

// ── Cases ─────────────────────────────────────────────────────────────────────

export async function loadCases(): Promise<Case[]> {
  // Try D1 first — gives cross-device sync
  const remote = await syncGet('/cases') as { cases?: Case[] } | null;
  if (remote?.cases && remote.cases.length > 0) {
    // Update local IndexedDB to match D1 (background, non-blocking)
    Promise.all(remote.cases.map(c => db.cases.put(c))).catch(() => {});
    return remote.cases;
  }
  // Fall back to IndexedDB
  try {
    return await db.cases.orderBy('createdAt').reverse().toArray();
  } catch (e) {
    console.error('[Storage] loadCases failed', e);
    return [];
  }
}

export async function loadCase(id: string): Promise<Case | null> {
  try {
    return await db.cases.get(id) ?? null;
  } catch (e) {
    console.error('[Storage] loadCase failed', e);
    return null;
  }
}

export async function saveCase(c: Case): Promise<boolean> {
  try {
    // Write to IndexedDB immediately
    await db.cases.put(c);
    // Sync to D1 in background
    syncPut('/case', c);
    return true;
  } catch (e) {
    console.error('[Storage] saveCase failed', e);
    return false;
  }
}

export async function deleteCase(id: string): Promise<boolean> {
  try {
    await db.transaction('rw',
      db.cases, db.docket_entries, db.deadlines,
      db.evidence_meta, db.evidence_files, db.blind_spots, db.research, db.arg_versions,
      async () => {
        await db.cases.delete(id);
        await db.docket_entries.where('caseId').equals(id).delete();
        await db.deadlines.where('caseId').equals(id).delete();
        const evIds = await db.evidence_meta
          .where('caseId').equals(id)
          .primaryKeys();
        await db.evidence_files.bulkDelete(evIds as string[]);
        await db.evidence_meta.where('caseId').equals(id).delete();
        await db.blind_spots.where('caseId').equals(id).delete();
        await db.research.where('caseId').equals(id).delete();
        await db.arg_versions.where('caseId').equals(id).delete();
      }
    );
    // Sync delete to D1 — this cascades entries/deadlines/research server-side
    syncDelete(`/case?id=${encodeURIComponent(id)}`);
    return true;
  } catch (e) {
    console.error('[Storage] deleteCase failed', e);
    return false;
  }
}

// ── Docket Entries ────────────────────────────────────────────────────────────

export async function loadEntries(caseId: string): Promise<DocketEntry[]> {
  const remote = await syncGet(`/entries?caseId=${encodeURIComponent(caseId)}`) as { entries?: DocketEntry[] } | null;
  if (remote?.entries && remote.entries.length > 0) {
    Promise.all(remote.entries.map(e => db.docket_entries.put(e))).catch(() => {});
    return remote.entries;
  }
  try {
    return await db.docket_entries
      .where('caseId').equals(caseId)
      .sortBy('dateFiled')
      .then(arr => arr.reverse());
  } catch (e) {
    console.error('[Storage] loadEntries failed', e);
    return [];
  }
}

export async function saveEntry(entry: DocketEntry): Promise<boolean> {
  try {
    await db.docket_entries.put(entry);
    syncPut('/entry', entry);
    return true;
  } catch (e) {
    console.error('[Storage] saveEntry failed', e);
    return false;
  }
}

export async function deleteEntry(id: string): Promise<boolean> {
  try {
    await db.docket_entries.delete(id);
    syncDelete(`/entry?id=${encodeURIComponent(id)}`);
    return true;
  } catch (e) {
    console.error('[Storage] deleteEntry failed', e);
    return false;
  }
}

// ── Deadlines ─────────────────────────────────────────────────────────────────

export async function loadDeadlines(caseId: string): Promise<Deadline[]> {
  const remote = await syncGet(`/deadlines?caseId=${encodeURIComponent(caseId)}`) as { deadlines?: Deadline[] } | null;
  if (remote?.deadlines && remote.deadlines.length > 0) {
    Promise.all(remote.deadlines.map(d => db.deadlines.put(d))).catch(() => {});
    return remote.deadlines;
  }
  try {
    return await db.deadlines.where('caseId').equals(caseId).sortBy('date');
  } catch (e) {
    console.error('[Storage] loadDeadlines failed', e);
    return [];
  }
}

export async function saveDeadline(dl: Deadline): Promise<boolean> {
  try {
    await db.deadlines.put(dl);
    syncPut('/deadline', dl);
    return true;
  } catch (e) {
    console.error('[Storage] saveDeadline failed', e);
    return false;
  }
}

export async function deleteDeadline(id: string): Promise<boolean> {
  try {
    await db.deadlines.delete(id);
    syncDelete(`/deadline?id=${encodeURIComponent(id)}`);
    return true;
  } catch (e) {
    console.error('[Storage] deleteDeadline failed', e);
    return false;
  }
}

// ── Evidence ──────────────────────────────────────────────────────────────────
// Metadata → D1 (synced). Files → R2 via Worker (synced). IndexedDB is local fallback.

export async function loadEvidenceMeta(caseId: string): Promise<EvidenceItem[]> {
  const remote = await syncGet(`/evidence/meta?caseId=${encodeURIComponent(caseId)}`) as { items?: EvidenceItem[] } | null;
  if (remote?.items && remote.items.length > 0) {
    Promise.all(remote.items.map(e => db.evidence_meta.put(e))).catch(() => {});
    return remote.items;
  }
  try {
    return await db.evidence_meta
      .where('caseId').equals(caseId)
      .sortBy('timestamp')
      .then(arr => arr.reverse());
  } catch (e) {
    console.error('[Storage] loadEvidenceMeta failed', e);
    return [];
  }
}

export async function saveEvidenceMeta(items: EvidenceItem[]): Promise<boolean> {
  try {
    await db.evidence_meta.bulkPut(items);
    // Sync each item to D1
    for (const item of items) {
      syncPut('/evidence/meta', item);
    }
    return true;
  } catch (e) {
    console.error('[Storage] saveEvidenceMeta failed', e);
    return false;
  }
}

export async function saveEvidenceFile(id: string, data: string, caseId: string): Promise<boolean> {
  try {
    // Save to IndexedDB immediately (local fallback)
    await db.evidence_files.put({ id, data });

    // Upload to R2 via Worker — convert base64 data URL to binary
    try {
      const base64 = data.includes(',') ? data.split(',')[1] : data;
      const mimeMatch = data.match(/data:([^;]+);/);
      const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const binary = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000); // 30s for file upload
      try {
        await fetch(`${WORKER_URL}/evidence/file?id=${encodeURIComponent(id)}&caseId=${encodeURIComponent(caseId)}`, {
          method: 'POST',
          headers: { ...syncHeaders(), 'Content-Type': mime },
          body: binary,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeout);
      }
    } catch {
      // R2 upload failed silently — file still in IndexedDB
    }

    return true;
  } catch (e) {
    console.error('[Storage] saveEvidenceFile failed — storage may be full', e);
    return false;
  }
}

export async function loadEvidenceFile(id: string, caseId?: string): Promise<string | null> {
  // Try R2 first if caseId provided
  if (caseId) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const res = await fetch(
          `${WORKER_URL}/evidence/file?id=${encodeURIComponent(id)}&caseId=${encodeURIComponent(caseId)}`,
          { method: 'GET', headers: syncHeaders(), signal: controller.signal }
        );
        if (res.ok) {
          const blob = await res.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
          });
        }
      } finally {
        clearTimeout(timeout);
      }
    } catch {
      // fall through to IndexedDB
    }
  }
  // Fallback to IndexedDB
  try {
    const rec = await db.evidence_files.get(id);
    return rec?.data ?? null;
  } catch (e) {
    console.error('[Storage] loadEvidenceFile failed', e);
    return null;
  }
}

export async function deleteEvidenceFile(id: string, caseId?: string): Promise<boolean> {
  try {
    await db.evidence_files.delete(id);
    await db.evidence_meta.delete(id);
    if (caseId) {
      syncDelete(`/evidence/file?id=${encodeURIComponent(id)}&caseId=${encodeURIComponent(caseId)}`);
      syncDelete(`/evidence/meta?id=${encodeURIComponent(id)}`);
    }
    return true;
  } catch (e) {
    console.error('[Storage] deleteEvidenceFile failed', e);
    return false;
  }
}

// ── Blind Spots ───────────────────────────────────────────────────────────────
// Blind spot data is session-specific analytical output — local only is fine.

export async function loadBlindSpot<T>(caseId: string, module: string, fallback?: T): Promise<T | null> {
  try {
    const rec = await db.blind_spots.get(`afs_bs_${module}_${caseId}`);
    return (rec?.data as T) ?? fallback ?? null;
  } catch (e) {
    console.error('[Storage] loadBlindSpot failed', e);
    return fallback ?? null;
  }
}

export async function saveBlindSpot(caseId: string, module: string, data: unknown): Promise<boolean> {
  try {
    await db.blind_spots.put({
      id: `afs_bs_${module}_${caseId}`,
      caseId,
      module,
      data,
    });
    return true;
  } catch (e) {
    console.error('[Storage] saveBlindSpot failed', e);
    return false;
  }
}

// ── Toulmin Migration (Build Plan Phase 1B) ────────────────────────────────────
// The IRAC → Toulmin rename (issue→claim, application→grounds, rule→warrant,
// plus new warrant_type/qualifier/rebuttal) only changes the ArgumentIssue
// TypeScript shape and every in-repo call site. It does nothing for issues
// that already exist as JSON inside `blind_spots` rows under module
// 'applications_v2' — those are opaque data blobs (SavedData → history →
// ApplicationRecord[] → stage3.issues[]) that Dexie/TypeScript never
// re-validates against the new interface. A saved record still has
// `{ issue, rule, application }` on disk until something rewrites it.
//
// This function is that one-time rewrite. It is:
//  - Idempotent: an issue that already has a `claim` string is left as-is
//    (only missing warrant_type/qualifier/rebuttal get backfilled), so it's
//    safe to run more than once or on a partially-migrated case.
//  - Dry-run by default: call with no args (or { apply: false }) to get a
//    report of what WOULD change without writing anything. Pass
//    { apply: true } once you've eyeballed the report — this matches the
//    plan's "run against a copy of live data first, diff before/after
//    counts" step; here the "copy" is just the dry-run pass against the
//    real DB, since nothing is mutated until apply:true.
//
// Not handled here (deliberately out of scope for 1B):
//  - `argument_templates.skeleton` — free-text markdown, not structured
//    issue/rule/application fields. There's nothing to mechanically rename;
//    any "Issue → Rule → Application" phrasing in old skeletons is prose
//    counsel wrote, not a data field, and rewriting prose isn't a safe
//    automated migration.
//  - FinalWrittenAddressEngine's IssueEntry — its `issues` state is local-only
//    React state (initialised fresh, never round-tripped through
//    loadBlindSpot/saveBlindSpot), so there is no persisted old-shape data
//    for it to migrate.

interface ToulminMigrationReport {
  dryRun:           boolean;
  caseIdsAffected:  string[];
  recordsScanned:   number;   // ApplicationRecord entries inspected
  recordsChanged:   number;   // ApplicationRecord entries with >=1 issue touched
  issuesScanned:    number;
  issuesRenamed:    number;   // had old issue/rule/application keys, renamed
  issuesBackfilled: number;   // already Toulmin-shaped, only missing defaults added
  errors:           { caseId: string; error: string }[];
}

/** Renames one raw stored issue object (on a shallow copy) and reports what happened. */
function migrateStoredIssue(raw: unknown): { issue: Record<string, unknown>; changed: boolean; renamed: boolean } {
  if (!raw || typeof raw !== 'object') {
    return { issue: (raw ?? {}) as Record<string, unknown>, changed: false, renamed: false };
  }
  const out: Record<string, unknown> = { ...(raw as Record<string, unknown>) };
  let changed = false;
  let renamed = false;

  if (typeof out.claim !== 'string' && typeof out.issue === 'string') {
    out.claim = out.issue;
    delete out.issue;
    changed = true; renamed = true;
  }
  if (typeof out.warrant !== 'string' && typeof out.rule === 'string') {
    out.warrant = out.rule;
    delete out.rule;
    changed = true; renamed = true;
  }
  if (typeof out.grounds !== 'string' && typeof out.application === 'string') {
    out.grounds = out.application;
    delete out.application;
    changed = true; renamed = true;
  }

  // Backfill new Toulmin-only fields with the documented defaults.
  if (out.warrant_type === undefined) { out.warrant_type = 'rule'; changed = true; }
  if (typeof out.qualifier !== 'string') { out.qualifier = ''; changed = true; }
  if (typeof out.rebuttal !== 'string')  { out.rebuttal  = ''; changed = true; }

  // Guard against any record that never had these as strings at all.
  if (typeof out.claim !== 'string')    { out.claim = ''; changed = true; }
  if (typeof out.grounds !== 'string')  { out.grounds = ''; changed = true; }
  if (typeof out.warrant !== 'string')  { out.warrant = ''; changed = true; }

  return { issue: out, changed, renamed };
}

/**
 * One-time transform for existing `applications_v2_${caseId}` blind-spot
 * records from IRAC (issue/rule/application) to Toulmin
 * (claim/grounds/warrant + warrant_type/qualifier/rebuttal).
 *
 * Usage:
 *   const report = await migrateToulminFields();               // dry run — inspect report
 *   const report = await migrateToulminFields({ apply: true }); // actually write
 */
export async function migrateToulminFields(opts: { apply?: boolean } = {}): Promise<ToulminMigrationReport> {
  const apply = opts.apply === true;
  const report: ToulminMigrationReport = {
    dryRun: !apply,
    caseIdsAffected: [],
    recordsScanned: 0,
    recordsChanged: 0,
    issuesScanned: 0,
    issuesRenamed: 0,
    issuesBackfilled: 0,
    errors: [],
  };

  let rows: BlindSpotRecord[] = [];
  try {
    rows = await db.blind_spots.where('module').equals('applications_v2').toArray();
  } catch (e) {
    report.errors.push({ caseId: '(query)', error: e instanceof Error ? e.message : String(e) });
    return report;
  }

  for (const row of rows) {
    try {
      const data = (row.data ?? {}) as { history?: unknown[] };
      const history = Array.isArray(data.history) ? data.history : [];
      let rowChanged = false;

      const newHistory = history.map((rawRecord) => {
        report.recordsScanned++;
        const appRecord = rawRecord as Record<string, unknown>;
        const stage3 = appRecord?.stage3 as Record<string, unknown> | undefined;
        const issues = stage3?.issues;
        if (!Array.isArray(issues)) return rawRecord;

        let recordChanged = false;
        const newIssues = issues.map((rawIssue) => {
          report.issuesScanned++;
          const { issue, changed, renamed } = migrateStoredIssue(rawIssue);
          if (changed) {
            recordChanged = true;
            if (renamed) report.issuesRenamed++; else report.issuesBackfilled++;
          }
          return issue;
        });

        if (!recordChanged) return rawRecord;
        rowChanged = true;
        report.recordsChanged++;
        return { ...appRecord, stage3: { ...stage3, issues: newIssues } };
      });

      if (rowChanged) {
        report.caseIdsAffected.push(row.caseId);
        if (apply) {
          await db.blind_spots.put({ ...row, data: { ...data, history: newHistory } });
        }
      }
    } catch (e) {
      report.errors.push({ caseId: row.caseId, error: e instanceof Error ? e.message : String(e) });
    }
  }

  return report;
}

// ── Research ──────────────────────────────────────────────────────────────────

export async function loadResearch(caseId: string): Promise<ResearchRecord[]> {
  const remote = await syncGet(`/research?caseId=${encodeURIComponent(caseId)}`) as { records?: ResearchRecord[] } | null;
  if (remote?.records && remote.records.length > 0) {
    Promise.all(remote.records.map(r => db.research.put(r))).catch(() => {});
    return remote.records;
  }
  try {
    return await db.research
      .where('caseId').equals(caseId)
      .sortBy('savedAt')
      .then(arr => arr.reverse());
  } catch (e) {
    console.error('[Storage] loadResearch failed', e);
    return [];
  }
}

export async function saveResearchItem(item: ResearchRecord): Promise<boolean> {
  try {
    await db.research.put(item);
    syncPut('/research', item);
    return true;
  } catch (e) {
    console.error('[Storage] saveResearchItem failed', e);
    return false;
  }
}

export async function deleteResearchItem(id: string): Promise<boolean> {
  try {
    await db.research.delete(id);
    syncDelete(`/research?id=${encodeURIComponent(id)}`);
    return true;
  } catch (e) {
    console.error('[Storage] deleteResearchItem failed', e);
    return false;
  }
}

// ── Argument Versions ─────────────────────────────────────────────────────────
// Argument versions are large analytical documents — local only for now.

export async function loadArgVersions(caseId: string): Promise<ArgumentVersion[]> {
  try {
    const rows = await db.arg_versions
      .where('caseId').equals(caseId)
      .sortBy('createdAt')
      .then(arr => arr.reverse());
    return rows.map(({ caseId: _c, ...v }) => v as ArgumentVersion);
  } catch (e) {
    console.error('[Storage] loadArgVersions failed', e);
    return [];
  }
}

export async function saveArgVersion(caseId: string, version: ArgumentVersion): Promise<boolean> {
  try {
    await db.arg_versions.put({ ...version, caseId });
    return true;
  } catch (e) {
    console.error('[Storage] saveArgVersion failed', e);
    return false;
  }
}

export async function deleteArgVersion(id: string): Promise<boolean> {
  try {
    await db.arg_versions.delete(id);
    return true;
  } catch (e) {
    console.error('[Storage] deleteArgVersion failed', e);
    return false;
  }
}

// ── Matrimonial Data ──────────────────────────────────────────────────────────
// Matrimonial structured state — stored in its own blindSpot slot with key
// 'matrimonial'. Never conflicts with other modules. Parallelise reads with
// Promise.all when loading case data.
//
// Key: afs_bs_matrimonial_<caseId>
// This deliberately reuses the blind_spots table as a generic key-value store
// so no schema migration is required. The 'matrimonial' module key is reserved
// and must never be used by any other module.

const MATRIMONIAL_MODULE = 'matrimonial';

export async function loadMatrimonialData(caseId: string): Promise<MatrimonialCaseData | null> {
  try {
    const rec = await db.blind_spots.get(`afs_bs_${MATRIMONIAL_MODULE}_${caseId}`);
    return (rec?.data as MatrimonialCaseData) ?? null;
  } catch (e) {
    console.error('[Storage] loadMatrimonialData failed', e);
    return null;
  }
}

export async function saveMatrimonialData(
  caseId: string,
  data: MatrimonialCaseData,
): Promise<boolean> {
  try {
    const now = new Date().toISOString();
    await db.blind_spots.put({
      id: `afs_bs_${MATRIMONIAL_MODULE}_${caseId}`,
      caseId,
      module: MATRIMONIAL_MODULE,
      data: { ...data, _updatedAt: now },
    });
    return true;
  } catch (e) {
    console.error('[Storage] saveMatrimonialData failed', e);
    return false;
  }
}

/**
 * Load matrimonial data in parallel with blind spots for efficient case load.
 * Usage:
 *   const [blindSpotData, matrimonialData] = await Promise.all([
 *     loadBlindSpot(caseId, 'someModule', fallback),
 *     loadMatrimonialData(caseId),
 *   ]);
 */
export async function loadCaseWithMatrimonialData(
  caseId: string,
): Promise<{ case: Case | null; matrimonialData: MatrimonialCaseData | null }> {
  const [caseRecord, matrimonialData] = await Promise.all([
    loadCase(caseId),
    loadMatrimonialData(caseId),
  ]);
  return { case: caseRecord, matrimonialData };
}

/**
 * Phase 8B — Token Telemetry
 *
 * Appends a token usage entry for a given case/engine call to IndexedDB.
 * Capped at 500 entries per case (oldest dropped first) so it never bloats.
 * Fire-and-forget — never throws to the caller.
 */
const TOKEN_LOG_CAP = 500;

export async function appendTokenLog(
  caseId: string,
  engine: string,
  usage: import('@/types').ApiUsage,
): Promise<void> {
  try {
    const key = `token_log_${caseId}`;
    const rec = await db.blind_spots.get(key);
    const existing: import('@/types').TokenLogEntry[] =
      Array.isArray((rec?.data as any)) ? (rec!.data as import('@/types').TokenLogEntry[]) : [];

    const entry: import('@/types').TokenLogEntry = {
      ts: new Date().toISOString(),
      engine,
      usage,
    };

    const updated = [...existing, entry].slice(-TOKEN_LOG_CAP);

    await db.blind_spots.put({
      id: key,
      caseId,
      module: 'token_log',
      data: updated,
    });
  } catch {
    // Always silent — telemetry must never surface errors to the user
  }
}

export async function loadTokenLog(caseId: string): Promise<import('@/types').TokenLogEntry[]> {
  try {
    const key = `token_log_${caseId}`;
    const rec = await db.blind_spots.get(key);
    return Array.isArray(rec?.data) ? (rec!.data as import('@/types').TokenLogEntry[]) : [];
  } catch {
    return [];
  }
}

 /**
 * Merges the intelligence fields into any existing MatrimonialCaseData without
 * overwriting structural fields (marriage_date, relief_type, children, etc.).
 * Increments intelligence_version on every call so engines can detect re-runs.
 *
 * Called twice by MIntelligence:
 *   1. After Step 2 extraction (intPackage = '') — makes extraction immediately
 *      available to other engines even if the associate stops early.
 *   2. After Step 5 package generation (intPackage = full narrative text).
 */
export async function writeIntelligenceToCase(
  caseId: string,
  extraction: MExtractionResult,
  intPackage: string,
): Promise<boolean> {
  try {
    const existing = await loadMatrimonialData(caseId) ?? {};
    await saveMatrimonialData(caseId, {
      ...existing,
      intelligence_extraction: extraction,
      intelligence_package:    intPackage || existing.intelligence_package,
      intelligence_run_at:     new Date().toISOString(),
      intelligence_version:    ((existing.intelligence_version ?? 0) + 1),
    });
    return true;
  } catch (e) {
    console.error('[Storage] writeIntelligenceToCase failed', e);
    return false;
  }
}

// ── Case Theory ───────────────────────────────────────────────────────────────
// Trial Engine Consolidation, Phase 1.
//
// The Case Theory record lives directly on the Case record (case_theory_*
// fields), not in its own table or blind_spots slot — it must be available
// wherever a Case is already loaded (TrialEngine, FinalWrittenAddressEngine,
// ArgumentBuilder, ApplicationsEngine) without an extra round trip.
//
// Every write goes through saveCase() so the existing dual-write (IndexedDB +
// D1 background sync) behaviour applies for free. Always read-modify-write
// against a freshly loaded Case to avoid clobbering unrelated fields that may
// have changed elsewhere (docket entries, deadlines, etc. are stored in their
// own tables, but intelligence_data / counsel_instructions / matter fields
// live on the same Case object).

export async function loadCaseTheory(caseId: string): Promise<CaseTheoryRecord | null> {
  try {
    const c = await loadCase(caseId);
    return c?.case_theory_structured ?? null;
  } catch (e) {
    console.error('[Storage] loadCaseTheory failed', e);
    return null;
  }
}

/**
 * Saves a theory draft. Does NOT lock it and does NOT touch case_theory_score —
 * score is written explicitly by the caller (after an AI re-score) as part of
 * theory.score_breakdown.total, read back out via hasCaseTheory()/loadCaseTheory().
 * Saving an already-locked theory is allowed (editing post-lock is still just
 * an edit) but does not itself change the lock state — call unlockCaseTheory()
 * first if the intent is to revise a locked theory.
 */
export async function saveCaseTheory(caseId: string, theory: CaseTheoryRecord): Promise<void> {
  try {
    const c = await loadCase(caseId);
    if (!c) {
      console.error('[Storage] saveCaseTheory failed — case not found', caseId);
      return;
    }
    await saveCase({
      ...c,
      case_theory_structured: theory,
      case_theory_score:      theory.score_breakdown?.total ?? c.case_theory_score ?? null,
    });
  } catch (e) {
    console.error('[Storage] saveCaseTheory failed', e);
  }
}

/**
 * Locks the current theory for propagation to downstream engines.
 * No-ops silently (with a console error) if no theory has been saved yet —
 * callers should disable the Lock button until case_theory_structured exists.
 * Increments case_theory_version on every lock (not on every save).
 */
export async function lockCaseTheory(caseId: string): Promise<void> {
  try {
    const c = await loadCase(caseId);
    if (!c) {
      console.error('[Storage] lockCaseTheory failed — case not found', caseId);
      return;
    }
    if (!c.case_theory_structured) {
      console.error('[Storage] lockCaseTheory failed — no theory to lock', caseId);
      return;
    }
    const now = new Date().toISOString();
    await saveCase({
      ...c,
      case_theory_locked:    true,
      case_theory_locked_at: now,
      case_theory_version:   (c.case_theory_version ?? 0) + 1,
    });
  } catch (e) {
    console.error('[Storage] lockCaseTheory failed', e);
  }
}

/**
 * Unlocks the theory so it can be revised. Requires a reason note — appended
 * to case_theory_history as a new entry pairing the lock/unlock timestamps
 * for that version. The theory record and score remain visible (callers
 * should show the amber "not locked" banner, not hide the data).
 */
export async function unlockCaseTheory(caseId: string, note: string): Promise<void> {
  try {
    const c = await loadCase(caseId);
    if (!c) {
      console.error('[Storage] unlockCaseTheory failed — case not found', caseId);
      return;
    }
    const now = new Date().toISOString();
    const historyEntry: CaseTheoryHistoryEntry = {
      version:     c.case_theory_version ?? 0,
      locked_at:   c.case_theory_locked_at ?? '',
      unlocked_at: now,
      note:        note || '(no reason given)',
    };
    await saveCase({
      ...c,
      case_theory_locked:    false,
      case_theory_history:   [...(c.case_theory_history ?? []), historyEntry],
    });
  } catch (e) {
    console.error('[Storage] unlockCaseTheory failed', e);
  }
}

/**
 * True only if the theory is locked AND the structured record exists.
 * This is the gate every downstream engine must check before reading
 * case_theory_structured — an unlocked or absent theory must never be
 * injected into a draft, even if the data is technically present.
 */
export async function hasCaseTheory(caseId: string): Promise<boolean> {
  try {
    const c = await loadCase(caseId);
    return !!(c?.case_theory_locked === true && c?.case_theory_structured);
  } catch (e) {
    console.error('[Storage] hasCaseTheory failed', e);
    return false;
  }
}

/**
 * Phase 0A — Intelligence Engine Gateway
 *
 * Returns true only when Intelligence Engine Step 5 is fully complete:
 *   1. intPkg is present (Step 5 primary output — the narrative package)
 *   2. risk_verdict is present (Step 5b — 8-dimension risk scoring)
 *   3. authority_grounding is present (Step 5c — hierarchy mapping + conflict flags)
 *
 * This is the gate that must be satisfied before CaseTheoryBriefTab allows
 * "Lock Theory". The dependency chain is:
 *   isIntelligenceComplete → Lock Theory → useCaseTheory → all consumers.
 *
 * Sync variant (isIntelligenceCompleteSync) accepts a Case object directly
 * for use in render paths where the case is already loaded (avoids a
 * redundant async loadCase call inside CaseTheoryBriefTab).
 */
export function isIntelligenceCompleteSync(c: import('@/types').Case | null | undefined): boolean {
  const intel = c?.intelligence_data;
  return !!(
    intel?.intPkg &&
    intel?.risk_verdict &&
    intel?.authority_grounding
  );
}

export async function isIntelligenceComplete(caseId: string): Promise<boolean> {
  try {
    const c = await loadCase(caseId);
    return isIntelligenceCompleteSync(c);
  } catch (e) {
    console.error('[Storage] isIntelligenceComplete failed', e);
    return false;
  }
}

// ── Argument Templates ───────────────────────────────────────────────────────
// Trial Engine Consolidation, Phase 2.
//
// Not scoped to a case — these live in their own global Dexie table
// (argument_templates) and are reused across every matter of the same
// appType/jurisdiction/court_level. Local-only, same as arg_versions:
// no D1 sync wired up yet for non-case-scoped tables.

export async function loadArgumentTemplates(): Promise<ArgumentTemplate[]> {
  try {
    return await db.argument_templates
      .orderBy('created_at')
      .reverse()
      .toArray();
  } catch (e) {
    console.error('[Storage] loadArgumentTemplates failed', e);
    return [];
  }
}

export async function saveArgumentTemplate(t: ArgumentTemplate): Promise<boolean> {
  try {
    await db.argument_templates.put(t);
    return true;
  } catch (e) {
    console.error('[Storage] saveArgumentTemplate failed', e);
    return false;
  }
}

export async function deleteArgumentTemplate(id: string): Promise<boolean> {
  try {
    await db.argument_templates.delete(id);
    return true;
  } catch (e) {
    console.error('[Storage] deleteArgumentTemplate failed', e);
    return false;
  }
}

/**
 * Case-insensitive, trimmed exact match on (appType, jurisdiction).
 * Returns the most recently updated match if more than one exists for the
 * same pair (shouldn't normally happen — the New Template form should
 * overwrite rather than duplicate — but this keeps lookups deterministic
 * either way). Returns null if no template exists for this combination —
 * callers should fall back to full RAG generation, exactly as they did
 * before templates existed.
 */
export async function findArgumentTemplate(
  appType: string,
  jurisdiction: string,
): Promise<ArgumentTemplate | null> {
  try {
    if (!appType.trim() || !jurisdiction.trim()) return null;
    const all = await db.argument_templates.toArray();
    const matches = all.filter(t =>
      t.appType.trim().toLowerCase() === appType.trim().toLowerCase() &&
      t.jurisdiction.trim().toLowerCase() === jurisdiction.trim().toLowerCase()
    );
    if (matches.length === 0) return null;
    matches.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    return matches[0];
  } catch (e) {
    console.error('[Storage] findArgumentTemplate failed', e);
    return null;
  }
}

// ── Rebuttal Bank ─────────────────────────────────────────────────────────────
// Toulmin Upgrade, Phase 5A schema / 5C access pattern. Same shape and lookup
// pattern as Argument Templates directly above — global table, keyed by
// (appType, jurisdiction), not scoped to a case.
//
// findRebuttalBank() is what Phase 5C's "check the bank first" merge logic
// calls before generateRebuttal() runs, so it can hand the model known
// defeaters to address-or-rule-out rather than rediscovering them each time.

export async function findRebuttalBank(
  appType: string,
  jurisdiction: string,
): Promise<RebuttalBankRecord | null> {
  try {
    if (!appType.trim() || !jurisdiction.trim()) return null;
    const id = `${appType.trim()}::${jurisdiction.trim()}`;
    return (await db.rebuttal_bank.get(id)) ?? null;
  } catch (e) {
    console.error('[Storage] findRebuttalBank failed', e);
    return null;
  }
}

export async function saveRebuttalBank(r: RebuttalBankRecord): Promise<boolean> {
  try {
    await db.rebuttal_bank.put(r);
    return true;
  } catch (e) {
    console.error('[Storage] saveRebuttalBank failed', e);
    return false;
  }
}

export async function deleteRebuttalBank(id: string): Promise<boolean> {
  try {
    await db.rebuttal_bank.delete(id);
    return true;
  } catch (e) {
    console.error('[Storage] deleteRebuttalBank failed', e);
    return false;
  }
}

/**
 * Append defeaters to an existing bank record, or create one if none exists
 * yet for this (appType, jurisdiction). Deduplicates by defeater text
 * (case-insensitive) — a re-seed or re-harvest won't produce duplicate rows.
 * This is the single write path both the starter set and real Phase 5B
 * harvesting should use, so provenance (source: 'seeded_unverified' vs
 * 'harvested') stays intact either way.
 */
export async function upsertRebuttalDefeaters(
  appType: string,
  jurisdiction: string,
  defeaters: RebuttalBankDefeater[],
): Promise<boolean> {
  try {
    const id = `${appType.trim()}::${jurisdiction.trim()}`;
    const now = new Date().toISOString();
    const existing = await db.rebuttal_bank.get(id);
    const existingKeys = new Set((existing?.defeaters ?? []).map(d => d.defeater.trim().toLowerCase()));
    const merged = [
      ...(existing?.defeaters ?? []),
      ...defeaters.filter(d => !existingKeys.has(d.defeater.trim().toLowerCase())),
    ];
    await db.rebuttal_bank.put({
      id,
      appType:    appType.trim(),
      jurisdiction: jurisdiction.trim(),
      defeaters:  merged,
      created_at: existing?.created_at ?? now,
      updated_at: now,
    });
    return true;
  } catch (e) {
    console.error('[Storage] upsertRebuttalDefeaters failed', e);
    return false;
  }
}

// ── Case Dissection Library ─────────────────────────────────────────────────
// Combined build plan ref: Part 2, "Persistence decision — Option B."
// Same shape/lookup convention as Argument Templates and Rebuttal Bank
// above — global table, local-only (no D1 sync wired up), keyed on a
// normalized string. Here the key is the citation, not an (appType,
// jurisdiction) pair. See CitedCaseLibraryEntry's doc comment in
// src/caseDissection/types.ts for why only the extraction (never the
// comparison/verdict) lives here.

/** Trimmed, lowercased — the only normalization applied to a citation before it becomes a lookup key. */
function normalizeCitationKey(citation: string): string {
  return citation.trim().toLowerCase();
}

/**
 * Looks up a library entry by citation. Returns null for an empty/blank
 * citation without touching storage — there is no reliable key to look up
 * a case that wasn't given a citation. This is the read path
 * dissectCitedCaseWithLibrary() (caseDissection/dissectCase.ts) calls
 * before deciding whether to re-extract or reuse, and is also what the
 * "already dissected, last used on [matter X]" UI hint calls directly as
 * counsel types a citation into the Dissect Cited Case panel.
 */
export async function findCaseDissectionLibraryEntry(
  citation: string,
): Promise<CitedCaseLibraryEntry | null> {
  try {
    const key = normalizeCitationKey(citation);
    if (!key) return null;
    return (await db.case_dissection_library.get(key)) ?? null;
  } catch (e) {
    console.error('[Storage] findCaseDissectionLibraryEntry failed', e);
    return null;
  }
}

/**
 * Saves (or overwrites) an extraction under its citation key. No-op —
 * returns false — for a blank citation, same reasoning as the lookup
 * above: nothing reliable to key it under. Overwrites are expected to be
 * rare (a citation's extraction shouldn't normally change) but are not
 * blocked; if counsel re-dissects a citation with a materially different
 * paste, the newer extraction wins.
 */
export async function saveCaseDissectionLibraryEntry(
  entry: CitedCaseLibraryEntry,
): Promise<boolean> {
  try {
    const key = normalizeCitationKey(entry.citation);
    if (!key) return false;
    await db.case_dissection_library.put({ ...entry, id: key });
    return true;
  } catch (e) {
    console.error('[Storage] saveCaseDissectionLibraryEntry failed', e);
    return false;
  }
}

/**
 * Records that a library entry's extraction was just reused on a
 * (possibly different) matter — updates last_used_matter_id/name/at only,
 * leaving the extraction itself untouched. Called every time
 * dissectCitedCaseWithLibrary() serves a cache hit, and also right after
 * a fresh extraction is first saved, so a brand-new entry immediately
 * shows a "last used on" matter rather than sitting blank until its
 * second use.
 */
export async function touchCaseDissectionLibraryUsage(
  citation: string,
  matterId: string,
  matterName: string,
): Promise<boolean> {
  try {
    const key = normalizeCitationKey(citation);
    if (!key) return false;
    const existing = await db.case_dissection_library.get(key);
    if (!existing) return false;
    await db.case_dissection_library.put({
      ...existing,
      last_used_matter_id:   matterId,
      last_used_matter_name: matterName,
      last_used_at:          new Date().toISOString(),
    });
    return true;
  } catch (e) {
    console.error('[Storage] touchCaseDissectionLibraryUsage failed', e);
    return false;
  }
}

export async function deleteCaseDissectionLibraryEntry(citation: string): Promise<boolean> {
  try {
    const key = normalizeCitationKey(citation);
    if (!key) return false;
    await db.case_dissection_library.delete(key);
    return true;
  } catch (e) {
    console.error('[Storage] deleteCaseDissectionLibraryEntry failed', e);
    return false;
  }
}

// ── Contract Engine — Client Position Profile (Roadmap 1f) ────────────────────
// Dual-write, same shape as loadCases/saveCase/deleteCase above: D1 read-
// through first (cross-device sync), IndexedDB write immediate + fallback
// when the Worker is unreachable. One profile per contract_id in practice,
// so load resolves to a single record (or null) rather than a list.

export async function loadClientPositionProfile(contractId: string): Promise<ClientPositionProfile | null> {
  const remote = await syncGet(`/client-position-profile?contractId=${encodeURIComponent(contractId)}`) as { profile?: ClientPositionProfile | null } | null;
  if (remote?.profile) {
    db.client_position_profile.put(remote.profile).catch(() => {});
    return remote.profile;
  }
  try {
    const rows = await db.client_position_profile.where('contract_id').equals(contractId).toArray();
    return rows[0] ?? null;
  } catch (e) {
    console.error('[Storage] loadClientPositionProfile failed', e);
    return null;
  }
}

export async function saveClientPositionProfile(profile: ClientPositionProfile): Promise<boolean> {
  try {
    await db.client_position_profile.put(profile);
    syncPut('/client-position-profile', profile);
    return true;
  } catch (e) {
    console.error('[Storage] saveClientPositionProfile failed', e);
    return false;
  }
}

export async function deleteClientPositionProfile(id: string): Promise<boolean> {
  try {
    await db.client_position_profile.delete(id);
    syncDelete(`/client-position-profile?id=${encodeURIComponent(id)}`);
    return true;
  } catch (e) {
    console.error('[Storage] deleteClientPositionProfile failed', e);
    return false;
  }
}

// ── Contract Engine — Contract Clauses (Roadmap 1f) ────────────────────────────
// Same dual-write pattern as loadEntries/saveEntry/deleteEntry above — many
// rows per contract_id, ordered by clause_number so Draft/Review/Negotiate
// UIs get a stable read order without re-sorting client-side.

export async function loadContractClauses(contractId: string): Promise<ContractClause[]> {
  const remote = await syncGet(`/contract-clauses?contractId=${encodeURIComponent(contractId)}`) as { clauses?: ContractClause[] } | null;
  if (remote?.clauses && remote.clauses.length > 0) {
    Promise.all(remote.clauses.map(c => db.contract_clauses.put(c))).catch(() => {});
    return remote.clauses;
  }
  try {
    return await db.contract_clauses
      .where('contract_id').equals(contractId)
      .sortBy('clause_number');
  } catch (e) {
    console.error('[Storage] loadContractClauses failed', e);
    return [];
  }
}

export async function saveContractClause(clause: ContractClause): Promise<boolean> {
  try {
    await db.contract_clauses.put(clause);
    syncPut('/contract-clause', clause);
    return true;
  } catch (e) {
    console.error('[Storage] saveContractClause failed', e);
    return false;
  }
}

export async function deleteContractClause(id: string): Promise<boolean> {
  try {
    await db.contract_clauses.delete(id);
    syncDelete(`/contract-clause?id=${encodeURIComponent(id)}`);
    return true;
  } catch (e) {
    console.error('[Storage] deleteContractClause failed', e);
    return false;
  }
}

/**
 * Deletes every clause for a contract locally. No bulk D1 route exists yet
 * for a cascade-on-parent-delete (mirrors the gap noted on deleteCase's
 * `research`/`arg_versions` local-only cleanup) — a future contract-delete
 * flow (5b) should loop deleteContractClause() per id if cloud cleanup is
 * needed, same as deleteCase does for docket_entries/deadlines today.
 */
export async function deleteContractClausesForContract(contractId: string): Promise<boolean> {
  try {
    await db.contract_clauses.where('contract_id').equals(contractId).delete();
    return true;
  } catch (e) {
    console.error('[Storage] deleteContractClausesForContract failed', e);
    return false;
  }
}

// ── Article Generator — Article Topics Log (Roadmap 7a/7b) ─────────────────────
// D1 only — no Dexie mirror table exists for this log (7a deliberately
// created the D1 table only, "no other tables yet"; there is no matching
// 1f-style Dexie step for the Article Generator on this roadmap). So unlike
// every other pair of helpers in this file, there is no IndexedDB fallback
// here: offline, loadArticleTopicsLog() returns [] and logArticleTopic()
// silently no-ops (same as every syncGet/syncPut failure elsewhere), rather
// than degrading to a local copy. Acceptable for a no-repeat/cross-area
// check that's advisory, not data of record.

export async function loadArticleTopicsLog(limit = 40): Promise<ArticleTopicLogEntry[]> {
  const remote = await syncGet(`/article-topics-log?limit=${encodeURIComponent(String(limit))}`) as { entries?: ArticleTopicLogEntry[] } | null;
  return remote?.entries ?? [];
}

export async function logArticleTopic(entry: ArticleTopicLogEntry): Promise<boolean> {
  try {
    await syncPut('/article-topic-log', entry);
    return true;
  } catch (e) {
    console.error('[Storage] logArticleTopic failed', e);
    return false;
  }
}

// ── Case Summary & Clone Draft ────────────────────────────────────────────────
// Trial Engine Consolidation, Phase 10D.
//
// loadAllCases() — lightweight case list for the Clone Draft target selector.
// cloneApplicationToCase() — copies an ApplicationRecord to a target case,
// clearing all case-specific content so counsel fills in only the new facts.
//
// Both are local-only (no D1 round trip): the selector doesn't need cross-
// device sync, and the clone write uses saveBlindSpot which is already local.

/**
 * Returns a lightweight summary of every case in the local IndexedDB, sorted
 * most-recently-created first. Used to populate the Clone Draft target
 * dropdown. No D1 round trip — the selector is a local UI concern.
 */
export async function loadAllCases(): Promise<CaseSummary[]> {
  try {
    const all = await db.cases.orderBy('createdAt').reverse().toArray();
    return all.map(c => ({
      id:            c.id,
      caseName:      c.caseName,
      matter_track:  c.matter_track,
      counsel_role:  c.counsel_role,
      jurisdiction:  c.court,   // court is the closest proxy for jurisdiction on the Case record
      createdAt:     c.createdAt,
    }));
  } catch (e) {
    console.error('[Storage] loadAllCases failed', e);
    return [];
  }
}

/**
 * Deep-copies an ApplicationRecord to a target case's Applications history.
 *
 * Preserved:  appType, facts object keys (structure only)
 * Cleared:    all fact values, stage3 (entirely case-specific), documents
 * Set:        new id, new caseId, new createdAt, _clone_notice in facts
 *
 * Saves via saveBlindSpot under module 'applications_v2' — the same key that
 * ApplicationsEngine uses (MODULE = 'applications_v2') — so the cloned record
 * appears in the target case's Saved Drafts panel immediately on next load.
 *
 * Returns the cloned record so the caller can reference its id for
 * navigation / display purposes.
 */
export async function cloneApplicationToCase(params: {
  sourceRecord:   CloneableApplicationRecord;
  targetCaseId:   string;
  sourceCaseName: string;
}): Promise<CloneableApplicationRecord> {
  const { sourceRecord, targetCaseId, sourceCaseName } = params;
  const now = new Date().toISOString();
  const dateLabel = new Date().toLocaleDateString('en-NG', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  // Clear every fact value; preserve the keys so the facts form renders all fields
  const clearedFacts: Record<string, string> = {};
  for (const key of Object.keys(sourceRecord.facts)) {
    clearedFacts[key] = '';
  }
  clearedFacts['_clone_notice'] =
    `Cloned from "${sourceCaseName}" on ${dateLabel} — review and update all facts before drafting.`;

  const cloned: CloneableApplicationRecord = {
    id:        uid(),
    caseId:    targetCaseId,
    appType:   sourceRecord.appType,
    facts:     clearedFacts,
    stage3:    {},    // Stage3Data is entirely case-specific — cleared on clone
    documents: '',    // Generated output is case-specific — never carries over
    createdAt: now,
  };

  // Prepend to the target case's existing Applications history
  const existing = await loadBlindSpot<{ history: CloneableApplicationRecord[] }>(
    targetCaseId, 'applications_v2', { history: [] },
  );
  const updated = { history: [cloned, ...(existing?.history ?? [])] };
  await saveBlindSpot(targetCaseId, 'applications_v2', updated);

  return cloned;
}


// ── Draft Buffer — Phase 7B resilient streaming ───────────────────────────────
//
// Three operations:
//   writeDraftChunk  — upsert the buffer record on every incoming SSE chunk
//   clearDraftBuffer — delete the record on clean stream completion
//   getStaleDraftBuffers — return records older than `maxAgeMs` for pruning
//
// Write strategy: we merge `partial` into the existing record rather than
// replacing the whole row on every chunk, but Dexie's put() replaces the full
// record atomically so we read first then write. To avoid a read-on-every-chunk
// overhead, callers accumulate text locally and call writeDraftChunk with the
// FULL assembled text so far — not just the latest delta. This means:
//   - The IndexedDB row always holds the complete partial, not a delta
//   - A resume path can read the row and get the full text immediately
//   - We pay one put() per chunk, but no read() in the hot path
//
// All three functions swallow errors silently — stream resilience must never
// crash the engine that initiated the call.

/** Upsert the in-flight partial text for a streaming call. */
export async function writeDraftChunk(record: DraftBufferRecord): Promise<void> {
  try {
    await db.draft_buffer.put(record);
  } catch {
    // silently ignored — losing a checkpoint is recoverable; crashing is not
  }
}

/** Delete the buffer record once a stream completes successfully. */
export async function clearDraftBuffer(callId: string): Promise<void> {
  try {
    await db.draft_buffer.delete(callId);
  } catch {
    // ignore
  }
}

/**
 * Return buffer records older than `maxAgeMs` milliseconds.
 * Default: 24 hours. Used by the startup pruner and the resume detector.
 */
export async function getStaleDraftBuffers(
  maxAgeMs = 24 * 60 * 60 * 1000,
): Promise<DraftBufferRecord[]> {
  try {
    const cutoff = new Date(Date.now() - maxAgeMs).toISOString();
    return await db.draft_buffer
      .where('startedAt')
      .below(cutoff)
      .toArray();
  } catch {
    return [];
  }
}

/**
 * Return ALL records in draft_buffer — used by Phase 7C resume detection
 * to check for any interrupted stream on app startup, regardless of age.
 */
export async function getAllDraftBuffers(): Promise<DraftBufferRecord[]> {
  try {
    return await db.draft_buffer.toArray();
  } catch {
    return [];
  }
}

// ── Media Library — Phase 8A ──────────────────────────────────────────────────
//
// Basic CRUD over the media_library table, plus saveMediaAsTemplate (8C),
// which flips an existing item's scope in place rather than copying it.
// Local-only, same as argument_templates: global-scoped items aren't tied
// to any one case, and the dual-write pattern isn't wired up for
// non-case-scoped tables yet.
//
// Remaining UI wiring — the global Asset Library panel, and merging case +
// global results into one EvidenceVault view — lands in Phases 8D–8E.

/** Upsert a single media item (case- or global-scoped). */
export async function saveMediaItem(item: MediaLibraryItem): Promise<boolean> {
  try {
    await db.media_library.put(item);
    return true;
  } catch (e) {
    console.error('[Storage] saveMediaItem failed', e);
    return false;
  }
}

/** Fetch a single media item by id, regardless of scope. */
export async function loadMediaItem(id: string): Promise<MediaLibraryItem | null> {
  try {
    return (await db.media_library.get(id)) ?? null;
  } catch (e) {
    console.error('[Storage] loadMediaItem failed', e);
    return null;
  }
}

/** All case-scoped items for one matter (scope:'case', caseId === caseId). */
export async function loadCaseMedia(caseId: string): Promise<MediaLibraryItem[]> {
  try {
    return await db.media_library
      .where('caseId')
      .equals(caseId)
      .toArray();
  } catch (e) {
    console.error('[Storage] loadCaseMedia failed', e);
    return [];
  }
}

/** All global/template-scoped items (scope:'global', caseId === null). */
export async function loadGlobalMedia(): Promise<MediaLibraryItem[]> {
  try {
    return await db.media_library
      .where('scope')
      .equals('global')
      .toArray();
  } catch (e) {
    console.error('[Storage] loadGlobalMedia failed', e);
    return [];
  }
}

/** Delete a single media item by id, regardless of scope. */
export async function deleteMediaItem(id: string): Promise<boolean> {
  try {
    await db.media_library.delete(id);
    return true;
  } catch (e) {
    console.error('[Storage] deleteMediaItem failed', e);
    return false;
  }
}

/**
 * "Save as Template" — Phase 8C.
 *
 * Promotes an existing media item to global/template scope in place:
 * flips `scope` to 'global' and clears `caseId` to null. This is the same
 * row, not a copy — once promoted, the item is no longer tied to the case
 * it was pasted into and becomes available to every matter via the global
 * Asset Library (Phase 8D).
 *
 * Returns the updated item, or null if the id doesn't exist. A no-op
 * (returns the item unchanged) if it's already global-scoped.
 */
export async function saveMediaAsTemplate(id: string): Promise<MediaLibraryItem | null> {
  try {
    const existing = await db.media_library.get(id);
    if (!existing) return null;
    if (existing.scope === 'global') return existing;

    const updated: MediaLibraryItem = { ...existing, scope: 'global', caseId: null };
    await db.media_library.put(updated);
    return updated;
  } catch (e) {
    console.error('[Storage] saveMediaAsTemplate failed', e);
    return null;
  }
}
